import React from "react";
import { Link } from "react-router-dom";
import { Heart, Users, GraduationCap, Home, Stethoscope, HeartHandshake, Building, Lightbulb, MapPin, Target, Eye, ArrowRight } from "lucide-react";

const About = () => {
  const objectives = [
    { icon: Heart, title: "Combating Poverty", description: "Working tirelessly to alleviate poverty and improve living conditions for vulnerable communities across Nigeria." },
    { icon: Stethoscope, title: "Providing Healthcare", description: "Ensuring access to quality healthcare services, paying medical bills, and providing essential medical supplies." },
    { icon: Users, title: "Supporting Children", description: "Nurturing and empowering children through orphanage visits, educational support, and creating lasting memories." },
    { icon: HeartHandshake, title: "Supporting the Elderly", description: "Caring for our senior citizens with dignity, compassion, and essential support services." },
    { icon: GraduationCap, title: "Providing Scholarships", description: "Offering educational scholarships to deserving students to help them achieve their academic dreams." },
    { icon: Building, title: "Educational Development", description: "Supporting schools and educational institutions to improve learning outcomes for all." },
    { icon: MapPin, title: "Community Development", description: "Building stronger communities through infrastructure support and social programs." },
    { icon: Lightbulb, title: "Promoting Entrepreneurship", description: "Empowering individuals with skills training and resources to start their own businesses." },
    { icon: Home, title: "Supporting Local Communities", description: "Working hand-in-hand with local communities to address their unique needs and challenges." },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-cream py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-primary font-medium text-sm tracking-wider uppercase mb-3">ABOUT US</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Changing Lives with Compassion & Purpose
            </h1>
            <p className="text-gray-600 text-lg">
              The Oshoke Alasa Foundation is a non-governmental organization dedicated to creating lasting positive change in communities across Nigeria.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-primary rounded-2xl p-10 text-white">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8" />
              </div>
              <h3 className="font-serif text-3xl font-bold mb-4">Our Mission</h3>
              <p className="text-white/90 leading-relaxed">
                To combat poverty, provide healthcare, support children and the elderly, and empower communities through education, entrepreneurship, and sustainable development programs. We believe that every act of kindness creates a ripple effect that transforms lives and builds stronger communities.
              </p>
            </div>
            <div className="bg-gray-900 rounded-2xl p-10 text-white">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8" />
              </div>
              <h3 className="font-serif text-3xl font-bold mb-4">Our Vision</h3>
              <p className="text-white/90 leading-relaxed">
                A Nigeria where every individual has access to quality healthcare, education, and opportunities to thrive. We envision communities free from poverty, where children grow up with hope, the elderly are cared for with dignity, and everyone has the chance to reach their full potential.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="grid grid-cols-2 gap-4">
              <img src="/gallery/img1.jpeg" alt="Foundation work" className="rounded-2xl shadow-lg w-full h-48 object-cover" />
              <img src="/gallery/img7.jpeg" alt="Community outreach" className="rounded-2xl shadow-lg w-full h-48 object-cover mt-8" />
              <img src="/gallery/img12.jpeg" alt="Healthcare support" className="rounded-2xl shadow-lg w-full h-48 object-cover" />
              <img src="/gallery/img20.jpeg" alt="Team work" className="rounded-2xl shadow-lg w-full h-48 object-cover mt-8" />
            </div>
            <div>
              <p className="text-primary font-medium text-sm tracking-wider uppercase mb-3">OUR STORY</p>
              <h2 className="font-serif text-4xl font-bold text-gray-900 mb-6">
                A Journey of Love and Service
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                The Oshoke Alasa Foundation was born out of a deep desire to make a meaningful difference in the lives of those who need it most. What started as small acts of kindness has grown into a movement of compassion that touches hundreds of lives across Nigeria.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                From visiting orphanages and creating lasting memories with children, to paying medical bills for patients in need, to training young women in valuable skills — every initiative is driven by our core belief that love in action can transform lives.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Today, we continue to expand our reach, partnering with communities, hospitals, and organizations to create sustainable impact and build a better future for all.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Aims & Objectives */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-primary font-medium text-sm tracking-wider uppercase mb-3">WHAT WE DO</p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Aims & Objectives</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              We are committed to creating lasting change through these key focus areas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {objectives.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mb-6">
                  <item.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-xl text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Make a Difference?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Join us in our mission to change lives and build stronger communities.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate" className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-full font-medium text-sm transition-colors inline-flex items-center gap-2">
              DONATE NOW <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-4 rounded-full font-medium text-sm transition-colors">
              CONTACT US
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
